import { compact } from '@seaber/common/lib/collections';
import type {
  BatchDataQuery,
  PlanningPageQuery,
  ShipmentsByIdsQuery,
} from '../graphql';

type Shipment = NonNullable<
  NonNullable<ShipmentsByIdsQuery['allShipments']>['nodes'][number]
>;

type PlanningPagePortCall = NonNullable<
  NonNullable<PlanningPageQuery['allPortCalls']>['nodes'][number]
>;

/**
 * Every top-level connection PlanningPage selects across
 * ScenarioTimeline_Query, PortCostEstimatesQuery and direct fields. Hermes
 * marks the query incomplete (so useQuery watchers don't broadcast) when any
 * of these is missing; a real fetch fills them all, and partial merges must too.
 */
const PLANNING_PAGE_CONNECTION_TYPENAMES = {
  allShips: 'ShipsConnection',
  allOrders: 'OrdersConnection',
  allScheduledOrders: 'ScheduledOrdersConnection',
  allHandledBySpots: 'HandledBySpotsConnection',
  allSingleEvents: 'SingleEventsConnection',
  allOffHires: 'OffHiresConnection',
  allCoaContracts: 'CoaContractsConnection',
  allHandledByCoas: 'HandledByCoasConnection',
  allOrganizations: 'OrganizationsConnection',
  allPortCalls: 'PortCallsConnection',
  allShipments: 'ShipmentsConnection',
  allPortCostsByPortAndVesselNames: 'PortCostsByPortAndVesselNamesConnection',
  allPortCostsByPortAndPrimaryAttributes:
    'PortCostsByPortAndPrimaryAttributesConnection',
  allLatestPortCostByPortAndPrimaryAttributes:
    'LatestPortCostByPortAndPrimaryAttributesConnection',
  allChannelCostEstimates: 'ChannelCostEstimatesConnection',
  allPortCostDefaults: 'PortCostDefaultsConnection',
  allZones: 'ZonesConnection',
  allAssortments: 'AssortmentsConnection',
  lastCommit: 'CommitLogsConnection',
} as const;

function appendDedupById<T extends { id: unknown }>(
  existing: readonly unknown[] | null | undefined,
  newNodes: readonly T[],
): T[] {
  // Last-write-wins (same id may appear in both inserted and updated).
  const dedupedNew = Array.from(
    new Map(newNodes.map((n) => [n.id, n])).values(),
  );
  const ids = new Set(dedupedNew.map((n) => n.id));
  const keep: T[] = [];
  for (const n of existing ?? []) {
    if (
      typeof n === 'object' &&
      n !== null &&
      'id' in n &&
      !ids.has((n as { id: unknown }).id)
    ) {
      keep.push(n as T);
    }
  }
  return [...keep, ...dedupedNew];
}

export interface BuildPlanningPageUpdatesArgs {
  dataInStore: PlanningPageQuery | null | undefined;
  batchData: BatchDataQuery | null | undefined;
  newShipments: readonly Shipment[];
}

/** Build partial PlanningPage cache updates from a BatchData response. Caller does the cache read/write. */
export function buildPlanningPageUpdates({
  dataInStore,
  batchData,
  newShipments,
}: BuildPlanningPageUpdatesArgs): Partial<PlanningPageQuery> {
  if (!dataInStore || !batchData) return {};

  const touchedOrders = [
    ...compact(batchData.inserted?.nodes ?? []),
    ...compact(batchData.updated?.nodes ?? []),
  ];
  const newCoas = compact([
    ...(batchData.insertedCoas?.nodes ?? []),
    ...(batchData.updatedCoas?.nodes ?? []),
  ]);
  const newScheduledOrders = compact(
    touchedOrders.flatMap((o) => o.scheduledOrdersByOrderId?.nodes ?? []),
  );
  const newNestedPortCalls = compact(
    touchedOrders
      .flatMap((o) => o.orderPortCallRelationsByOrderId?.nodes ?? [])
      .map((r) => r?.portCallByPortCallId),
  );

  const existingPortCalls = compact(dataInStore.allPortCalls?.nodes ?? []);
  const existingPortCallsById = new Map(
    existingPortCalls.map((pc) => [pc.id, pc]),
  );
  /**
   * BatchData omits the Files subtree on nested port calls. Stub empty for
   * new rows; preserve cached files on updates.
   */
  const portCallsToMerge: PlanningPagePortCall[] = newNestedPortCalls.map(
    (pc) => ({
      ...pc,
      portCallFilesByPortCallId: existingPortCallsById.get(pc.id)
        ?.portCallFilesByPortCallId ?? {
        __typename: 'PortCallFilesConnection',
        nodes: [],
      },
    }),
  );

  const updates: Partial<PlanningPageQuery> = {};

  if (touchedOrders.length > 0) {
    updates.allOrders = {
      __typename: 'OrdersConnection',
      ...dataInStore.allOrders,
      nodes: appendDedupById(dataInStore.allOrders?.nodes, touchedOrders),
    };
  }
  if (newCoas.length > 0) {
    updates.allCoaContracts = {
      __typename: 'CoaContractsConnection',
      ...dataInStore.allCoaContracts,
      nodes: appendDedupById(dataInStore.allCoaContracts?.nodes, newCoas),
    };
  }
  if (portCallsToMerge.length > 0) {
    updates.allPortCalls = {
      __typename: 'PortCallsConnection',
      ...dataInStore.allPortCalls,
      nodes: appendDedupById(dataInStore.allPortCalls?.nodes, portCallsToMerge),
    };
  }
  if (newScheduledOrders.length > 0) {
    updates.allScheduledOrders = {
      __typename: 'ScheduledOrdersConnection',
      ...dataInStore.allScheduledOrders,
      nodes: appendDedupById(
        dataInStore.allScheduledOrders?.nodes,
        newScheduledOrders,
      ),
    };
  }
  // Shipments arrive two ways: order-linked (ShipmentsByIds follow-up) and
  // order-less updates (BatchData.updatedShipments, e.g. a Sedna sync touching
  // a shipment but no order)
  const mergedShipments = [
    ...newShipments,
    ...compact(batchData.updatedShipments?.nodes ?? []),
  ];
  if (mergedShipments.length > 0) {
    updates.allShipments = {
      __typename: 'ShipmentsConnection',
      ...dataInStore.allShipments,
      nodes: appendDedupById(dataInStore.allShipments?.nodes, mergedShipments),
    };
  }

  return updates;
}

/**
 * Stub every PlanningPage connection that's missing or unreadable in
 * `dataInStore`. Without this Hermes marks the query incomplete and
 * useQuery watchers never re-emit, leaving the UI frozen on pre-merge state.
 */
export function completePlanningPageConnections(
  dataInStore: PlanningPageQuery | null | undefined,
): Partial<PlanningPageQuery> {
  if (!dataInStore) return {};
  const out: Partial<PlanningPageQuery> = {};
  for (const [key, typename] of Object.entries(
    PLANNING_PAGE_CONNECTION_TYPENAMES,
  )) {
    const existing = dataInStore[key as keyof PlanningPageQuery] as
      | { nodes?: readonly unknown[] | null }
      | null
      | undefined;
    // Hermes returns ref-shaped (non-array) `nodes` sometimes; treat as gone.
    if (existing == null || !Array.isArray(existing.nodes)) {
      (out as Record<string, unknown>)[key] = {
        __typename: typename,
        nodes: [],
      };
    }
  }
  return out;
}

/**
 * Shipment ids linked from touched orders that aren't already cached.
 * ShipmentFilter doesn't support related-order filters, so the handler
 * issues a follow-up ShipmentsByIds query with this list.
 */
export function extractShipmentIdsToFetch(
  batchData: BatchDataQuery | null | undefined,
  existingShipmentIds: ReadonlySet<number>,
): number[] {
  if (!batchData) return [];
  const touched = [
    ...compact(batchData.inserted?.nodes ?? []),
    ...compact(batchData.updated?.nodes ?? []),
  ];
  const ids = new Set<number>();
  for (const order of touched) {
    if (
      order.shipmentId != null &&
      !existingShipmentIds.has(order.shipmentId)
    ) {
      ids.add(order.shipmentId);
    }
  }
  return Array.from(ids);
}
